import { ChakraProvider, ColorModeScript } from '@chakra-ui/react';
import * as Sentry from '@sentry/react';
import { QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';

import { useAuthenticationStore } from '$/components/core/Authentication/stores/useAuthenticationStore';
import { envVars } from '$/envVars';
import { enablePiwik, trackEvent } from '$/logger';
import { router } from '$/routes';
import { configureAccessToken } from '$/services/accessToken';
import { queryClient } from '$/services/client';
import { setupWebviewAuthIntegration } from '$/services/usecases/authentication';
import { configureCollectionTracking } from '$/stores/collectionTracking';
import { theme, config } from '$/theme';

import 'overlayscrollbars/overlayscrollbars.css';
import './theme/styles.css';

if (!new URLSearchParams(window.location.search).has('isWebview')) {
  const cookiebotScript = document.createElement('script');
  cookiebotScript.id = 'Cookiebot';
  cookiebotScript.src = 'https://consent.cookiebot.com/uc.js';
  cookiebotScript.setAttribute(
    'data-cbid',
    '1a68e49c-76fc-4646-827f-10158996e910',
  );
  cookiebotScript.type = 'text/javascript';
  document.head.appendChild(cookiebotScript);
}

Sentry.init({
  dsn: envVars.VITE_SENTRY_DSN,
  environment: envVars.VITE_NODE_ENV,
  tracesSampleRate: 0,
  dataCollection: { userInfo: false },
});

enablePiwik();
configureAccessToken(() => useAuthenticationStore.getState().accessToken);
configureCollectionTracking({ trackEvent });
setupWebviewAuthIntegration();

createRoot(document.getElementById('root') as HTMLElement, {
  onUncaughtError: Sentry.reactErrorHandler(),
  onCaughtError: Sentry.reactErrorHandler(),
  onRecoverableError: Sentry.reactErrorHandler(),
}).render(
  <StrictMode>
    <ColorModeScript initialColorMode={config.initialColorMode} />
    <ChakraProvider theme={theme}>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
      </QueryClientProvider>
    </ChakraProvider>
  </StrictMode>,
);
